/**
 * SectionRenderer — renders one section: resolve component by `kind`, validate
 * `layout` against the registry (fallback to the kind's default), then render inside a
 * per-section error boundary + suspense with the resolved `SectionComponentProps`. There
 * is NO variant resolver and NO variantId — a kind maps to exactly one component and the
 * layout is a CSS-only selector. Unknown kind → dev warning + skip. See BUILDER_ENGINE_V2.md.
 */
import { Suspense } from 'react'
import type { Section } from '../schema'
import { ErrorBoundary } from './errors/ErrorBoundary'
import { BrokenSection } from './fallback/BrokenSection'
import { useRendererContext } from './hooks'
import type { SectionComponentProps } from './types'

export function SectionRenderer({ section }: { readonly section: Section }) {
  const { registry, theme, locale, assets, config } = useRendererContext()

  const entry = registry.entry(section.kind)
  if (!entry) {
    if (import.meta.env?.DEV) {
      console.warn(`[builder] Unknown section kind "${section.kind}" — skipping.`)
    }
    return null
  }
  const Component = entry.component

  // Registry is the single source of truth for layout validity; fall back to its default.
  const layout = registry.isValidLayout(section.kind, section.layout)
    ? section.layout
    : registry.defaultLayout(section.kind) ?? section.layout

  const props: SectionComponentProps = {
    id: section.id,
    kind: section.kind,
    layout,
    align: section.align,
    visible: section.visible,
    content: section.content,
    theme,
    locale,
    assets,
  }

  return (
    <ErrorBoundary
      fallback={(error) => <BrokenSection sectionId={section.id} error={error} />}
      onError={(error) =>
        config.onError?.(error, { sectionId: section.id, kind: section.kind, layout: section.layout })
      }
    >
      <Suspense fallback={null}>
        <Component {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}
